<script lang="ts">
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import Check from '@athena-lib/Icons/Check.svelte';
  import clsx from 'clsx';
  import CloseSmall from '@athena-lib/Icons/CloseSmall.svelte';
  import { getPasswordScore, type PasswordScore } from './utils';

  let { input, isValid = $bindable() }: { input: string; isValid?: boolean } = $props();

  const translations = getCommonTranslations();

  const result = $derived(getPasswordScore(input));
  const booleanKeys = $derived(
    Object.entries(result)
      .filter(([_, value]) => typeof value === 'boolean')
      .map(([name]) => name),
  ) as Array<keyof PasswordScore>;

  const numberOfOkParts = $derived(booleanKeys.filter((key) => result[key]).length);
  const allOk = $derived(booleanKeys.length === numberOfOkParts);
  const barColor = $derived.by(() => {
    if (numberOfOkParts === 0) {
      return '';
    }
    if (allOk) {
      return 'bg-st-green text-st-white';
    }
    if (numberOfOkParts === 1) {
      return 'bg-st-red-800 text-st-white';
    }
    return 'bg-st-yellow text-st-black';
  });

  $effect(() => {
    if (allOk) {
      isValid = allOk;
    }
  });
</script>

{#snippet booleanCheck(text: string, value: boolean)}
  <div
    class={clsx(
      'flex flex-row items-center rounded text-st-white',
      value ? ' bg-st-green' : 'bg-st-red-600',
    )}
  >
    {#if value}
      <Check sizeClass={'h-6 w-6 '} />
    {:else}
      <CloseSmall sizeClass={'h-6 w-6'} />
    {/if}
    {text}
  </div>
{/snippet}

<Stack space="none" class="gap-1 text-xs font-bold">
  <StackItem>{translations.passwordStrengthMeter}</StackItem>
  <StackItem>
    <div class="flex flex-row rounded shadow-inner">
      <div
        class={clsx(
          barColor,
          'rounded p-1 text-center',
          numberOfOkParts === booleanKeys.length ? '' : 'rounded-e-none',
        )}
        style={`width:${(numberOfOkParts / booleanKeys.length) * 100}%`}
      >
        {numberOfOkParts}/{booleanKeys.length}
      </div>
    </div>
  </StackItem>
  {#if result.invalidChars?.length ?? 0 > 0}
    <StackItem>{translations.invalidCharacter}: {result.invalidChars}</StackItem>
  {/if}
  <StackItem>{translations.yourPasswordShouldInclude}</StackItem>
  <StackItem>{@render booleanCheck(translations.atLeast12Characters, result.length)}</StackItem>
  <StackItem
    >{@render booleanCheck(
      translations.uppercaseAndLowercaseLetters,
      result.upperLowerCase,
    )}</StackItem
  >
  <StackItem>{@render booleanCheck(translations.atLeastOneNumber, result.number)}</StackItem>
  <StackItem
    >{@render booleanCheck(translations.atLeastOneSpecialCharacter, result.special)}</StackItem
  >
</Stack>
